import { getCurrentInstance } from 'vue';
import { Page } from '@superthread-com/api';
import { nanoid } from 'nanoid';
import useOpenPage from '@/utilities/composables/useOpenPage';
import useTranslate from '@/utilities/composables/useTranslate';
import constants from '@/utilities/constants';

export default function useDuplicatePage() {
  const _this = getCurrentInstance()?.proxy;
  if (!_this) return;

  const { openPage } = useOpenPage() || {};
  const { translate } = useTranslate();

  const duplicatePage = (page: Page) => {
    const localId = nanoid(6);
    const pageTitle = page.title || translate('pageTitlePlaceholder');
    const newPage = {
      title: `${translate('copyOf')} - ${pageTitle}`,
      content: page.content,
      id: localId,
      localPageId: true,
      team_id: page.team_id,
      project_id: page.project_id,
    };

    _this.$store.dispatch('setPages', {
      [newPage.id]: newPage,
    });

    _this.$store.dispatch('addPageToProjectPages', {
      pageId: localId,
      projectId: newPage.project_id,
    });

    const projectPageTree = _this.$store.getters.getProjectPagesTree(page.project_id) || {};
    const pageFromTree = projectPageTree[page.id!];
    if (!projectPageTree.root || !pageFromTree) return;
    const pagePosition = projectPageTree[pageFromTree.parent]?.children.findIndex(
      (id: any) => id === page.id
    );

    _this.$store.dispatch('setParentPageIdForAdd', pageFromTree.parent);
    _this.$store.dispatch('localAddPageInPageTree', {
      projectId: page.project_id,
      pageId: localId,
      position: pagePosition + 1,
    });

    _this.$store
      .dispatch('duplicatePage', {
        pageId: page.id,
        teamId: page.team_id,
        projectId: page.project_id,
        title: `${translate('copyOf')} - ${pageTitle}`,
        position: pagePosition + 1,
      })
      .then(({ page }: { page: any }) => {
        _this.$store.dispatch('addPageToPagesList', {
          localId,
          page,
        });
        _this.$store
          .dispatch('localRemovePageInTree', {
            projectId: newPage.project_id,
            pageId: localId,
          })
          .then(() => {
            _this.$store.dispatch('localAddPageInPageTree', {
              projectId: page.project_id,
              pageId: page.id,
              position: pagePosition + 1,
            });
            _this.$store.dispatch('localAddPageInProject', {
              projectId: page.project_id,
              page,
            });
          });
        _this.$nextTick(() => {
          _this.$store.dispatch('deleteFromPages', {
            localId,
            page,
          });
        });

        _this.$store.dispatch('addUiNotification', {
          message: translate('pageDuplicated'),
          icon: 'copy',
          labels: [
            {
              text: translate('open'),
              type: constants.uiNotificationElements.button,
              callback: openPage,
              buttonStyle: 'important',
              params: page,
            },
          ],
        });
      })
      .catch((/* err */) => {
        _this.$store.dispatch('localRemovePageInTree', {
          projectId: newPage.project_id,
          pageId: localId,
        });
        _this.$store.dispatch('deleteFromPages', {
          localId,
        });
        _this.$store.dispatch('addUiNotification', {
          message: translate('pageDuplicateFailed'),
          status: constants.uiNotificationStatuses.error,
          duration: 4000,
        });
      });
  };

  return {
    duplicatePage,
  };
}
